import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Typography,
    Tooltip,
    IconButton,
    Menu,
    MenuItem,
    Hidden,
    Theme
} from '@material-ui/core';
import { StyleRules, createStyles, withStyles } from '@material-ui/core/styles';
import Icon from '@mdi/react';
import {
    mdiAccountCircle,
    mdiHelpCircle,
    mdiMenu
} from '@mdi/js';
import TrimbleAppLauncher from '@tcx/trimble-app-launcher/lib/AppLauncher';

import AuthService from '../../services/authService';
import { getAuthorizedMenuList, marketInsightsRoutes, truckLoadRateRoutes } from '../../routes/appRouteList';
import { AppRoles } from '../../interfaces/authorizationInterfaces';
import TrimbleLogo from './trimbleLogo';
import HomeButton from '../buttons/homeButton';
import AppHeaderMenu from './appHeaderMenu';

interface AppHeaderProps {
    renderFullHeader: boolean;
    roles: AppRoles;
    homeRoute: string;
    classes?: any;
}

const styles = (theme: Theme): StyleRules => {
    return createStyles({
        appLogoWrapper: {
            paddingRight: theme.spacing(1)
        },
        appTitleWrapper: {
            display: 'flex',
            flexGrow: 1,
            paddingTop: '4px'
        },
        appTitle: {
            fontSize: '16px'
        },
        appTitleDivider: {
            paddingTop: '3px'
        },
        [theme.breakpoints.only('xs')]: {
            appLogoWrapper: {
                flexGrow: 1
            }
        }
    });
};

const AppHeader: React.FunctionComponent<AppHeaderProps> = (props: AppHeaderProps): JSX.Element => {
    const history = useHistory();
    const [anchorEl, setAnchorEl] = useState(null);
    const [isAppNavMenuOpen, setAppNavMenuOpen] = useState(false);

    const appMenuList = getAuthorizedMenuList(props.roles);

    const handleAccountMenuOpen = (event: any): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleAccountMenuClose = (): void => {
        setAnchorEl(null);
    };

    const handleProfileMenu = (): void => {
        if (props.roles.marketInsights) {
            history.push(marketInsightsRoutes.profile);
        } else if (props.roles.truckLoadRate || props.roles.truckLoadRateAdmin) {
            history.push(truckLoadRateRoutes.profile);
        }
        handleAccountMenuClose();
    };

    const handleContactUs = (): void => {
        window.open('https://transportation.trimble.com/contact-us', '_blank');
    };

    return (
        <AppBar position='static' className={props.classes.appHeader}>
            <Toolbar variant='dense'>
                <IconButton
                    edge='start'
                    color='inherit'
                    onClick={(): void => {
                        setAppNavMenuOpen(true);
                    }}
                >
                    <Icon path={mdiMenu} size={1} />
                </IconButton>
                <AppHeaderMenu
                    menuList={appMenuList}
                    isMenuOpen={isAppNavMenuOpen}
                    updateIsMenuOpen={(isMenuOpen: boolean): void => { setAppNavMenuOpen(isMenuOpen); }}
                />
                <div className={props.classes.appLogoWrapper}>
                    <HomeButton tooltipTitle='Back to Home' homeRoute={props.homeRoute}>
                        <TrimbleLogo />
                    </HomeButton>
                </div>
                <Hidden only='xs'>
                    <div className={props.classes.appTitleWrapper}>
                        {props.roles.marketInsights === true &&
                        <Typography variant='h6' color='inherit' className={props.classes.appTitle}>
                            Market Insights
                        </Typography>}
                        {props.roles.marketInsights === false &&
                        <Typography variant='h6' color='inherit' className={props.classes.appTitle}>
                            TruckLoadRate.com
                        </Typography>}
                    </div>
                </Hidden>
                <Tooltip title='Contact Us'>
                    <IconButton color='inherit' onClick={handleContactUs}>
                        <Icon path={mdiHelpCircle} size={1} />
                    </IconButton>
                </Tooltip>
                {
                    props.renderFullHeader &&
                    <TrimbleAppLauncher />
                }
                <Tooltip title='Account'>
                    <IconButton
                        color='inherit'
                        aria-controls='menu-appbar-account'
                        aria-haspopup='true'
                        onClick={handleAccountMenuOpen}
                    >
                        <Icon path={mdiAccountCircle} size={1} />
                    </IconButton>
                </Tooltip>
                <Menu
                    id='menu-appbar-account'
                    anchorEl={anchorEl}
                    getContentAnchorEl={null}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleAccountMenuClose}
                >
                    <MenuItem
                        onClick={(): void => {
                            handleProfileMenu();
                        }}
                    >
                        Profile
                    </MenuItem>
                    <MenuItem
                        onClick={(): void => {
                            handleAccountMenuClose();
                            AuthService.logout();
                        }}
                    >
                        Log Out
                    </MenuItem>
                </Menu>
            </Toolbar>
        </AppBar>
    );
};

const mapStateToProps = (state: any): object => {
    return {
        roles: state.userDetails.roles,
        homeRoute: state.userDetails.homeRoute
    };
};

const mergeProps = (stateProps: any, dispatchProps: any, ownProps: any): object => {
    return {
        ...ownProps,
        ...stateProps,
        ...dispatchProps
    };
};

export default connect(mapStateToProps, null, mergeProps)(withStyles(styles)(AppHeader));
